import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, Button, Flex, Icon, Input, Spinner, Text, VStack,
} from "@chakra-ui/react";
import { HiOutlineLockClosed } from "react-icons/hi2";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_API_URL } from "../../Components/config";
import { saveSession } from "../../Components/auth/session";
import { c, useClassroomColorMode } from "../classroomColors";
import "../classroom-theme.css";

/**
 * Landing point for the "free practice paper" email link.
 *
 * The link carries a token, and holding it shows the person reached the inbox
 * we sent to — the same thing a confirmation email checks. So someone new can
 * choose a password here and go straight into the paper, instead of being sent
 * to sign in and then made to wait on a second verification email. That gap is
 * where most of the people who asked for a paper used to disappear.
 *
 * Signed in already -> claim and go, as before.
 * Address already has an account -> sign in. A forwarded link must never hand
 *   somebody else's account to whoever opened it.
 * No token (an email sent before this existed) -> the old sign-in route, which
 *   still works.
 *
 * Already submitted -> the classroom dashboard, where the assignment card shows
 * whether feedback is still pending or ready to read.
 */

const SUBJECT_KEY = "freePaperSubject";
const API = `${BASE_API_URL}/api/v1`;

/** Both spellings are accepted so older emails keep working. */
function readSubject(search) {
  const params = new URLSearchParams(search);
  return (
    params.get("subject") ||
    params.get("free_paper") ||
    sessionStorage.getItem(SUBJECT_KEY) ||
    ""
  );
}

export default function FreePaperClaim() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark } = useClassroomColorMode();

  const [error, setError] = useState(null);
  // null until we know which screen this is: a password step or a redirect.
  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState("");
  const [fieldError, setFieldError] = useState(null);
  const [working, setWorking] = useState(false);

  // Claiming is not free of side effects, so it must not run twice if React
  // re-mounts this component (StrictMode does exactly that in development).
  const started = useRef(false);

  const subject = readSubject(location.search);
  const startToken = new URLSearchParams(location.search).get("t");

  /** Fetch the questions and open the paper. Shared by both routes in. */
  const openPaper = useCallback(
    async (claimed, headers) => {
      sessionStorage.removeItem(SUBJECT_KEY);

      if (claimed.already_submitted) {
        navigate("/classroom/student/dashboard", { replace: true });
        return;
      }

      const { data: questions } = await axios.get(
        `${API}/user-paper-attempts/questions-with-attempt/${claimed.user_paper_attempt_guid}`,
        { headers }
      );

      navigate("/my-papers/solve-paper", {
        replace: true,
        state: {
          questions,
          paper_Guid: claimed.user_paper_attempt_guid,
          subject_name: claimed.subject_name,
          formattedPaperName: `Free practice paper — ${claimed.subject_name} (${claimed.subject_code})`,
          time_hour: 0,
          time_min: 0,
          paperMode: "CLASSROOM",
          viewerRole: "STUDENT",
          feedbackReleased: false,
        },
      });
    },
    [navigate]
  );

  const begin = useCallback(async () => {
    const token = localStorage.getItem("accessToken");
    const tokenType = localStorage.getItem("tokenType") || "Bearer";

    // Already signed in: nothing has changed for them.
    if (token) {
      if (!subject) {
        setError(
          "That link is missing its subject. Please use the link in your email."
        );
        return;
      }
      const headers = { Authorization: `${tokenType} ${token}` };
      try {
        const { data: claimed } = await axios.post(
          `${API}/practice-paper-requests/claim`,
          { subject_code: subject },
          { headers }
        );
        await openPaper(claimed, headers);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401) {
          // Token expired between the email and the click.
          sessionStorage.setItem(SUBJECT_KEY, subject);
          navigate("/login", { replace: true });
          return;
        }
        setError(
          status === 404
            ? "We don't have a free paper ready for that subject yet. We'll email you as soon as it is."
            : "We couldn't open your paper just now. Please try the link again, or reply to our email."
        );
      }
      return;
    }

    // Signed out with no token: an email from before this existed. The old
    // route still works, so send them to sign in rather than to a dead end.
    if (!startToken) {
      if (subject) sessionStorage.setItem(SUBJECT_KEY, subject);
      navigate("/login", { replace: true });
      return;
    }

    try {
      const { data } = await axios.get(
        `${API}/practice-paper-requests/start/${startToken}`
      );
      if (data.account_exists) {
        // Never auto-sign-in an address that already belongs to someone.
        sessionStorage.setItem(SUBJECT_KEY, data.subject_code || subject);
        navigate("/login", { replace: true });
        return;
      }
      setInvite(data);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "We couldn't open that link. Please try the most recent email we sent you."
      );
    }
  }, [navigate, openPaper, subject, startToken]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    begin();
  }, [begin]);

  async function submitPassword(event) {
    event.preventDefault();
    setFieldError(null);

    if (password.trim().length < 8) {
      setFieldError("Please choose at least 8 characters.");
      return;
    }

    setWorking(true);
    try {
      const { data } = await axios.post(
        `${API}/practice-paper-requests/start/${startToken}`,
        { password }
      );
      saveSession(data);
      await openPaper(data, {
        Authorization: `${data.token_type || "bearer"} ${data.access_token}`,
      });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        // Signed up in another tab between opening the link and submitting.
        sessionStorage.setItem(SUBJECT_KEY, invite?.subject_code || subject);
        navigate("/login", { replace: true });
        return;
      }
      setFieldError(
        err?.response?.data?.detail ||
          "We couldn't set up your account just now. Please try again."
      );
      setWorking(false);
    }
  }

  const shell = (children) => (
    <Box
      className="stande-classroom"
      data-theme={isDark ? "dark" : "light"}
      minHeight="100vh"
      bg={c.page}
      display="flex"
      alignItems="center"
      justifyContent="center"
      px="20px"
    >
      {children}
    </Box>
  );

  if (error) {
    return shell(
      <VStack spacing="12px" textAlign="center" maxW="440px">
        <Text fontSize="19px" fontWeight="500" color={c.text}>
          Something went wrong
        </Text>
        <Text fontSize="14px" fontWeight="300" color={c.textSub}>
          {error}
        </Text>
        <Button
          mt="6px" size="sm" h="34px" px="14px" borderRadius="8px"
          bg={c.solidBg} color={c.solidText} fontSize="13px" fontWeight="500"
          _hover={{ opacity: 0.88 }}
          onClick={() => navigate("/login")}
        >
          Go to sign in
        </Button>
      </VStack>
    );
  }

  // The one step between the email and the paper.
  if (invite) {
    return shell(
      <Box
        as="form"
        onSubmit={submitPassword}
        bg={c.surface}
        border="1px solid"
        borderColor={c.borderSoft}
        borderRadius="16px"
        p={{ base: "24px", md: "32px" }}
        maxW="420px"
        width="100%"
      >
        <Flex
          boxSize="40px" borderRadius="full" bg={c.surfaceSubtle}
          align="center" justify="center" mb="16px"
        >
          <Icon as={HiOutlineLockClosed} boxSize="18px" color={c.textMuted} />
        </Flex>

        <Text fontSize="21px" fontWeight="500" color={c.text} lineHeight="1.3">
          Your {invite.subject_name} paper is ready
        </Text>
        <Text fontSize="14px" fontWeight="300" color={c.textSub} mt="6px" mb="20px">
          Choose a password and we&apos;ll take you straight to it. You&apos;ll
          use this to come back for your report.
        </Text>

        <Text fontSize="12px" color={c.textMuted} mb="5px">
          Email
        </Text>
        <Input
          value={invite.email}
          isReadOnly
          mb="14px"
          h="42px" borderRadius="10px" bg={c.surfaceSubtle}
          border="1px solid" borderColor={c.inputBorder}
          color={c.textSub} fontSize="14px"
          _focus={{ boxShadow: "none", borderColor: c.inputBorder }}
        />

        <Text fontSize="12px" color={c.textMuted} mb="5px">
          Choose a password
        </Text>
        <Input
          type="password"
          value={password}
          autoFocus
          autoComplete="new-password"
          placeholder="At least 8 characters"
          onChange={(e) => {
            setPassword(e.target.value);
            if (fieldError) setFieldError(null);
          }}
          h="42px" borderRadius="10px" bg={c.inputBg}
          border="1px solid" borderColor={fieldError ? "#d93025" : c.inputBorder}
          color={c.text} fontSize="14px"
          _placeholder={{ color: c.textMuted }}
          _focus={{ borderColor: c.textMuted, boxShadow: "none" }}
        />
        {fieldError && (
          <Text fontSize="12.5px" color="#d93025" mt="7px">
            {fieldError}
          </Text>
        )}

        <Button
          type="submit"
          isLoading={working}
          loadingText="Setting up"
          mt="20px" width="100%" h="44px" borderRadius="10px"
          bg={c.solidBg} color={c.solidText} fontSize="14px" fontWeight="500"
          _hover={{ opacity: 0.88 }}
        >
          Start the paper
        </Button>

        <Text fontSize="12px" fontWeight="300" color={c.textMuted} mt="14px" textAlign="center">
          It is not timed. Submit when you are done and a teacher marks it
          within 24 hours.
        </Text>
      </Box>
    );
  }

  return shell(
    <VStack spacing="14px">
      <Spinner color={c.textMuted} />
      <Text fontSize="14px" fontWeight="300" color={c.textSub}>
        Opening your paper…
      </Text>
    </VStack>
  );
}
